"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const THREE = __importStar(require("three"));
const fiber_1 = require("@react-three/fiber");
const drei_1 = require("@react-three/drei");
const ComplexMapping = ({ fn }) => {
    const gridRef = (0, react_1.useRef)(null);
    const [showOriginal, setShowOriginal] = (0, react_1.useState)(true);
    // Create grid with better spacing and colors
    const gridLines = [];
    const size = 5;
    const steps = 8; // Reduced from 20 for better clarity
    const density = 0.2; // Line sampling density
    // Color gradient for transformation intensity
    const getColor = (dist) => {
        const intensity = Math.min(1, dist / 10);
        return new THREE.Color(0.5 + intensity * 0.5, // R
        0.7 - intensity * 0.5, // G
        0.8 - intensity * 0.3 // B
        );
    };
    // Create grid points
    const points = [];
    const colors = [];
    // Horizontal lines
    for (let y = -size; y <= size; y += size / steps) {
        for (let x = -size; x <= size; x += density) {
            const z = fn({ x, y });
            points.push(new THREE.Vector3(x, y, 0));
            points.push(new THREE.Vector3(z.x, z.y, 0));
            const dist = Math.sqrt(z.x * z.x + z.y * z.y);
            const color = getColor(dist);
            colors.push(color, color);
        }
    }
    // Vertical lines
    for (let x = -size; x <= size; x += size / steps) {
        for (let y = -size; y <= size; y += density) {
            const z = fn({ x, y });
            points.push(new THREE.Vector3(x, y, 0));
            points.push(new THREE.Vector3(z.x, z.y, 0));
            const dist = Math.sqrt(z.x * z.x + z.y * z.y);
            const color = getColor(dist);
            colors.push(color, color);
        }
    }
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors.flatMap(c => [c.r, c.g, c.b]), 3));
    gridLines.push(react_1.default.createElement("lineSegments", { geometry: geometry },
        react_1.default.createElement("lineBasicMaterial", { vertexColors: true, linewidth: 2 })));
    return (react_1.default.createElement("group", { ref: gridRef }, showOriginal && gridLines));
};
const ComplexMappingVisualization = () => {
    const [selectedFn, setSelectedFn] = (0, react_1.useState)('z^2');
    const functions = {
        'z^2': {
            fn: (z) => ({ x: z.x * z.x - z.y * z.y, y: 2 * z.x * z.y }),
            formula: 'f(z) = z²'
        },
        'sin(z)': {
            fn: (z) => ({ x: Math.sin(z.x) * Math.cosh(z.y), y: Math.cos(z.x) * Math.sinh(z.y) }),
            formula: 'f(z) = sin(z)'
        },
        'exp(z)': {
            fn: (z) => ({ x: Math.exp(z.x) * Math.cos(z.y), y: Math.exp(z.x) * Math.sin(z.y) }),
            formula: 'f(z) = eᶻ'
        },
        '1/z': {
            fn: (z) => {
                const denom = z.x * z.x + z.y * z.y;
                return { x: z.x / denom, y: -z.y / denom };
            },
            formula: 'f(z) = 1/z'
        },
        'sqrt(z)': {
            fn: (z) => {
                const r = Math.sqrt(z.x * z.x + z.y * z.y);
                const theta = Math.atan2(z.y, z.x);
                return {
                    x: Math.sqrt(r) * Math.cos(theta / 2),
                    y: Math.sqrt(r) * Math.sin(theta / 2)
                };
            },
            formula: 'f(z) = √z'
        },
        'log(z)': {
            fn: (z) => ({
                x: Math.log(z.x * z.x + z.y * z.y) / 2,
                y: Math.atan2(z.y, z.x)
            }),
            formula: 'f(z) = ln(z)'
        },
        'z^3': {
            fn: (z) => ({
                x: z.x * z.x * z.x - 3 * z.x * z.y * z.y,
                y: 3 * z.x * z.x * z.y - z.y * z.y * z.y
            }),
            formula: 'f(z) = z³'
        },
        'tan(z)': {
            fn: (z) => {
                const denom = Math.cos(2 * z.x) + Math.cosh(2 * z.y);
                return {
                    x: Math.sin(2 * z.x) / denom,
                    y: Math.sinh(2 * z.y) / denom
                };
            },
            formula: 'f(z) = tan(z)'
        },
        'z + 1/z': {
            fn: (z) => {
                const denom = z.x * z.x + z.y * z.y;
                return {
                    x: z.x + z.x / denom,
                    y: z.y - z.y / denom
                };
            },
            formula: 'f(z) = z + 1/z'
        },
        'e^z + z': {
            fn: (z) => ({
                x: Math.exp(z.x) * Math.cos(z.y) + z.x,
                y: Math.exp(z.x) * Math.sin(z.y) + z.y
            }),
            formula: 'f(z) = eᶻ + z'
        },
        'z^2 + c': {
            fn: (z) => ({
                x: z.x * z.x - z.y * z.y - 0.8,
                y: 2 * z.x * z.y + 0.2 // c = 0.2i
            }),
            formula: 'f(z) = z² + c'
        }
    };
    const [animationProgress, setAnimationProgress] = (0, react_1.useState)(0);
    const [isAnimating, setIsAnimating] = (0, react_1.useState)(false);
    const prevFnRef = (0, react_1.useRef)(selectedFn);
    // Animation effect
    (0, react_1.useEffect)(() => {
        if (!isAnimating)
            return;
        const duration = 1000; // 1 second animation
        const startTime = performance.now();
        const animate = (time) => {
            const elapsed = time - startTime;
            const progress = Math.min(elapsed / duration, 1);
            setAnimationProgress(progress);
            if (progress < 1) {
                requestAnimationFrame(animate);
            }
            else {
                setIsAnimating(false);
                prevFnRef.current = selectedFn;
            }
        };
        requestAnimationFrame(animate);
    }, [isAnimating, selectedFn]);
    const handleFunctionChange = (fnKey) => {
        if (fnKey !== selectedFn) {
            prevFnRef.current = selectedFn;
            setSelectedFn(fnKey);
            setIsAnimating(true);
            setAnimationProgress(0);
        }
    };
    // Interpolate between functions during animation
    const getCurrentFn = () => {
        if (!isAnimating)
            return functions[selectedFn].fn;
        const prevFn = functions[prevFnRef.current].fn;
        const nextFn = functions[selectedFn].fn;
        return (z) => {
            const p1 = prevFn(z);
            const p2 = nextFn(z);
            return {
                x: p1.x + (p2.x - p1.x) * animationProgress,
                y: p1.y + (p2.y - p1.y) * animationProgress
            };
        };
    };
    return (react_1.default.createElement("div", { className: "complex-mapping" },
        react_1.default.createElement("div", { className: "visualization-header" },
            react_1.default.createElement("h1", null, "Complex Mappings"),
            react_1.default.createElement("p", null, "Visualize how complex functions transform the plane through conformal mappings.")),
        react_1.default.createElement("div", { className: "controls", style: { marginBottom: '20px' } },
            react_1.default.createElement("div", { className: "control-group" },
                react_1.default.createElement("label", null, "Function:"),
                react_1.default.createElement("select", { value: selectedFn, onChange: (e) => handleFunctionChange(e.target.value), style: { marginRight: '10px' } }, Object.keys(functions).map(fn => (react_1.default.createElement("option", { key: fn, value: fn }, fn)))),
                react_1.default.createElement("span", { style: {
                        fontFamily: 'monospace',
                        fontSize: '1.2em',
                        whiteSpace: 'nowrap',
                        display: 'inline-block'
                    } }, functions[selectedFn].formula)),
            isAnimating && (react_1.default.createElement("div", { className: "animation-progress" },
                react_1.default.createElement("progress", { value: animationProgress, max: 1, style: { width: '100%' } })))),
        react_1.default.createElement("div", { className: "info-panel", style: { marginTop: '20px', padding: '15px', background: '#1e293b', borderRadius: '8px' } },
            react_1.default.createElement("h3", null, "How it works"),
            react_1.default.createElement("p", null, "Each point z = x + iy in the complex plane is transformed by the selected function."),
            react_1.default.createElement("p", null, "During animation, you can see how the mapping smoothly transitions between functions."),
            react_1.default.createElement("p", null, "The color intensity shows how much each point is being stretched or compressed.")),
        react_1.default.createElement("div", { className: "canvas-container", style: { height: '500px', width: '100%' } },
            react_1.default.createElement(fiber_1.Canvas, { camera: { position: [0, 0, 10], fov: 50 }, style: { height: '100%', width: '100%' } },
                react_1.default.createElement("ambientLight", { intensity: 0.5 }),
                react_1.default.createElement(ComplexMapping, { fn: getCurrentFn() }),
                react_1.default.createElement(drei_1.OrbitControls, { enableZoom: true, enablePan: true, minDistance: 5, maxDistance: 20 }),
                react_1.default.createElement("gridHelper", { args: [10, 10, 0x444444, 0x888888], rotation: [Math.PI / 2, 0, 0] }),
                react_1.default.createElement("axesHelper", { args: [5] }))),
        react_1.default.createElement("div", { className: "explanation", style: {
                marginTop: '40px',
                padding: '20px',
                background: '#1e293b',
                borderRadius: '8px'
            } },
            react_1.default.createElement("h3", { style: { marginBottom: '15px', color: '#f8fafc' } }, "What am I looking at?"),
            react_1.default.createElement("p", { style: { lineHeight: '1.6', color: '#e2e8f0' } }, "This visualization shows how complex functions transform the complex plane. Each point z = x + iy is mapped to a new point f(z) in the plane. The grid lines help visualize how different regions are transformed."),
            react_1.default.createElement("p", { style: { lineHeight: '1.6', color: '#e2e8f0', marginTop: '10px' } },
                react_1.default.createElement("strong", null, "Key observations:")),
            react_1.default.createElement("ul", { style: { lineHeight: '1.6', color: '#e2e8f0', marginTop: '10px' } },
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "z\u00B2"),
                    ": Squares the magnitude and doubles the angle (conformal except at 0)"),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "sin(z)"),
                    ": Periodic in real direction, exponential growth in imaginary direction"),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "exp(z)"),
                    ": Wraps vertical lines into circles and horizontal lines into rays"),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "1/z"),
                    ": Inversion through unit circle combined with reflection")),
            react_1.default.createElement("p", { style: { lineHeight: '1.6', color: '#e2e8f0', marginTop: '10px' } }, "Complex functions preserve angles locally (conformal mapping) except at critical points."))));
};
exports.default = ComplexMappingVisualization;
